import i18next from 'i18next';
import moment from 'moment-timezone';
import React from 'react';

import theme from '@theme';

import SpinnerIcon from '@assets/icons/new-loader.svg';

import { DATE_DISPLAY_FORMATS } from '@commons/DatePickers/constants';
import { Label } from '@commons/utils/styledLibraryComponents';
import { LoadingIcon } from '@commons/Metrics/components/Stats/styledComponents';
import DisplayNumber from '@commons/DisplayNumber';

import { getClientStoreNameTranslation } from '@commons/utils/translations';
import { LabelContainer } from '../styledComponents';

export const formatDate = (date, userLanguageCode) =>
  moment(date).format(
    userLanguageCode === 'us'
      ? DATE_DISPLAY_FORMATS.SLASHED_MONTH_DAY_YEAR
      : DATE_DISPLAY_FORMATS.SLASHED_DAY_MONTH_YEAR,
  );

const renderRecomputingLoader = () => (
  <LoadingIcon alt="recomputing" height="16px" src={SpinnerIcon} width="16px" />
);

export const getListColumns = (userLanguageCode, storeName) => [
  {
    id: 'supplierName',
    propertyKey: 'supplierName',
    name: i18next.t('GENERAL.SUPPLIER'),
  },
  {
    id: 'storeName',
    propertyKey: 'storeName',
    name: getClientStoreNameTranslation(storeName),
  },
  {
    id: 'createdAt',
    propertyKey: 'createdAt',
    filterType: 'date',
    excludeFromSearch: true,
    name: i18next.t('INVOICE.INVOICE_CONTROLS.INVOICE_CONTROLS_IMPORT_DATE'),
    render: (date) => formatDate(date, userLanguageCode),
    hidden: true, // we keep it for advanced filter
  },
  {
    id: 'invoiceDate',
    propertyKey: 'invoiceDate',
    filterType: 'date',
    excludeFromSearch: true,
    name: i18next.t('INVOICE.INVOICE_CONTROLS.INVOICE_CONTROLS_INVOICE_DATE'),
    render: (invoiceDate) => (!!invoiceDate ? formatDate(invoiceDate, userLanguageCode) : '-'),
  },
  {
    id: 'invoiceNumber',
    propertyKey: 'invoiceNumber',
    name: i18next.t('INVOICE.INVOICE_CONTROLS.INVOICE_CONTROLS_INVOICE_NUMBER'),
    render: (invoiceNumber) => (!!invoiceNumber ? invoiceNumber : '-'),
  },
  {
    id: 'total',
    propertyKey: 'total',
    filterType: 'numeric',
    excludeFromSearch: true,
    name: i18next.t('INVOICE.INVOICE_CONTROLS.INVOICE_CONTROLS_INVOICED_TOT_EX_TAX'),
    render: (item) => (!!item ? <DisplayNumber displayCurrencyCode={true} number={item} /> : '-'),
  },
  {
    id: 'computedOrderReferences',
    propertyKey: 'computedOrderReferences', // to make search on this field possible
    filterType: 'numeric',
    name: i18next.t('INVOICE.INVOICE_CONTROLS.INVOICE_CONTROLS_ASSOCIATED_ORDERS'),
    render: (_, row) => {
      if (row?.isRecomputing) {
        return renderRecomputingLoader();
      }

      const count = row.orders.length;

      const isWarning = count === 0;

      return (
        <LabelContainer>
          <Label
            background={isWarning ? theme.colors.infoRed : theme.colors.infoGreen}
            color={isWarning ? theme.colors.infoRed : theme.colors.infoGreen}
            type={null}
          >
            {count}
          </Label>
        </LabelContainer>
      );
    },
  },
  {
    id: 'gapExcludingTax',
    propertyKey: 'gapExcludingTax',
    filterType: 'numeric',
    excludeFromSearch: true,
    name: i18next.t('INVOICE.INVOICE_CONTROLS.TOTAL_GAP_EXCL_TAXES'),
    tooltipText: i18next.t('INVOICE.INVOICE_CONTROLS.GAP_EXCL_TAXES_COLUMN_TOOLTIP'),
    render: (item, row) => {
      if (row?.isRecomputing) {
        return renderRecomputingLoader();
      }

      if (item != null) {
        return <DisplayNumber displayCurrencyCode={true} number={item} />;
      }

      return '-';
    },
  },
  {
    id: 'lineGap',
    propertyKey: 'lineGap',
    filterType: 'numeric',
    excludeFromSearch: true,
    name: i18next.t('INVOICE.INVOICE_CONTROLS.LINE_GAP_EXCL_TAXES'),
    tooltipText: i18next.t('INVOICE.INVOICE_CONTROLS.LINE_GAP_EXCL_TAXES_COLUMN_TOOLTIP'),
    render: (item, row) => {
      if (row?.isRecomputing) {
        return renderRecomputingLoader();
      }

      if (item != null) {
        return <DisplayNumber displayCurrencyCode={true} number={item} />;
      }

      return '-';
    },
  },
];
